import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { AnimatePresence } from "framer-motion";
import { Loader2 } from "lucide-react";
import { api, apiError } from "../lib/api";
import { FunnelIntro } from "./funnel/FunnelIntro";
import { FunnelQuiz, FunnelContact } from "./funnel/FunnelSteps";
import { FunnelLoading, FunnelResult, FunnelChat } from "./funnel/FunnelResultChat";

export default function PublicFunnel() {
  const { slug } = useParams();
  const [funnel, setFunnel] = useState(null);
  const testimonials = useMemo(() => (funnel?.testimonials || []).filter((t) => t?.text), [funnel]);
  const [notFound, setNotFound] = useState(false);

  const [stage, setStage] = useState("intro"); // intro | quiz | contact | loading | result | chat
  const [qIndex, setQIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [openAnswer, setOpenAnswer] = useState("");
  const [contact, setContact] = useState({ name: "", phone: "", email: "", message: "" });
  const [result, setResult] = useState(null);

  // Chat IA (Respuesta Especializada de IA)
  const [chat, setChat] = useState({ sessionId: null, messages: [], ended: false, qualified: false, whatsapp: "", secondsLeft: 300 });
  const [chatInput, setChatInput] = useState("");
  const [sending, setSending] = useState(false);

  const aiChat = !!funnel?.ai_chat_available;

  useEffect(() => {
    api.get(`/public/funnels/${slug}`)
      .then((r) => setFunnel(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (stage !== "chat" || chat.ended) return;
    const t = setInterval(() => {
      setChat((p) => (p.secondsLeft > 0 ? { ...p, secondsLeft: p.secondsLeft - 1 } : p));
    }, 1000);
    return () => clearInterval(t);
  }, [stage, chat.ended]);

  const questions = funnel?.questions || [];
  const hasQuiz = questions.length > 0;

  const selectChoice = (q, opt) => {
    setAnswers((p) => ({ ...p, [q.id]: opt }));
    setTimeout(() => nextQuestion(), 250);
  };

  const nextQuestion = () => {
    const q = questions[qIndex];
    if (q?.type === "text") {
      if (!openAnswer.trim()) return toast.error("Escribe tu respuesta");
      setAnswers((p) => ({ ...p, [q.id]: openAnswer.trim() }));
      setOpenAnswer("");
    }
    if (qIndex < questions.length - 1) setQIndex(qIndex + 1);
    else setStage("contact");
  };

  const submit = async () => {
    if (!contact.name.trim() || !contact.phone.trim()) return toast.error("Nombre y WhatsApp son obligatorios");
    if (aiChat) return startChat();
    setStage("loading");
    const answersArr = questions.map((q) => ({ question: q.text, answer: answers[q.id] || "" }));
    try {
      const { data } = await api.post(`/public/funnels/${slug}/leads`, { ...contact, answers: answersArr });
      setResult(data);
      setStage("result");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setStage("contact");
    }
  };

  const startChat = async () => {
    setStage("loading");
    try {
      const { data } = await api.post(`/public/funnels/${slug}/chat/start`, {
        name: contact.name.trim(), phone: contact.phone.trim(), email: contact.email,
      });
      setChat({ sessionId: data.session_id, messages: [{ role: "assistant", content: data.message }], ended: false, qualified: false, whatsapp: "", secondsLeft: data.seconds_limit || 300 });
      setStage("chat");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setStage("contact");
    }
  };

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text || sending || chat.ended) return;
    setChatInput("");
    setChat((p) => ({ ...p, messages: [...p.messages, { role: "user", content: text }] }));
    setSending(true);
    try {
      const { data } = await api.post(`/public/chat/${chat.sessionId}`, { text });
      setChat((p) => ({
        ...p,
        messages: [...p.messages, { role: "assistant", content: data.reply }],
        ended: !!data.ended,
        qualified: !!data.qualified,
        whatsapp: data.whatsapp_invite || "",
        secondsLeft: data.seconds_left ?? p.secondsLeft,
      }));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (notFound)
    return <div className="min-h-screen bg-black flex items-center justify-center text-zinc-400">Este embudo no existe.</div>;
  if (!funnel)
    return <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="animate-spin text-purple-500" size={32} /></div>;

  const progress = hasQuiz ? Math.round(((qIndex + (stage === "contact" || stage === "result" ? 1 : 0)) / (questions.length + 1)) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#050505] grid-texture text-white flex items-center justify-center px-5 py-12">
      <div className="w-full max-w-lg">
        {/* progress bar */}
        {hasQuiz && stage !== "intro" && stage !== "result" && (
          <div className="mb-6">
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-purple-500 to-yellow-400 transition-all duration-500" style={{ width: `${Math.max(progress, 8)}%` }} />
            </div>
          </div>
        )}

        <AnimatePresence mode="wait">
          {stage === "intro" && (
            <FunnelIntro
              funnel={funnel} testimonials={testimonials} aiChat={aiChat} hasQuiz={hasQuiz}
              questionsCount={questions.length}
              onStart={() => setStage(aiChat ? "contact" : (hasQuiz ? "quiz" : "contact"))}
            />
          )}
          {stage === "quiz" && hasQuiz && (
            <FunnelQuiz
              questions={questions} qIndex={qIndex} answers={answers}
              openAnswer={openAnswer} setOpenAnswer={setOpenAnswer}
              selectChoice={selectChoice} nextQuestion={nextQuestion}
            />
          )}
          {stage === "contact" && (
            <FunnelContact funnel={funnel} hasQuiz={hasQuiz} contact={contact} setContact={setContact} submit={submit} />
          )}
          {stage === "loading" && <FunnelLoading />}
          {stage === "result" && result && <FunnelResult result={result} />}
          {stage === "chat" && (
            <FunnelChat chat={chat} chatInput={chatInput} setChatInput={setChatInput} sending={sending} sendChat={sendChat} />
          )}
        </AnimatePresence>

        <p className="text-center text-xs text-zinc-600 mt-6">Powered by MULTICONTACTOS</p>
      </div>
    </div>
  );
}
